import { appConfig as defaultAppConfig } from '../app-config.js';
import { CONTRACT_VERSION } from '../inference/types.js';
import { normalizeSearch, resolveClientMode } from '../inference/runtime.js';
import {
  armConfig,
  armsForExperiment,
  isArm,
  isExperiment,
  EXPERIMENTS,
} from '../../shared/experiment-arms.js';

function stableParticipantId(prolificId) {
  let hash = 2166136261;
  for (const ch of prolificId) {
    hash ^= ch.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return `mock-${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

function stableIndex(value, size) {
  let hash = 2166136261;
  for (const ch of value) {
    hash ^= ch.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) % size;
}

function readArmOverride(search = '') {
  try {
    const params = new URLSearchParams(normalizeSearch(search));
    const arm = params.get('arm');
    return isArm(arm) ? arm : null;
  } catch {
    return null;
  }
}

// Dev/E2E affordance ONLY. In production the experiment is resolved server-side
// from the Prolific STUDY_ID; the participant-facing URL carries no `exp`. But
// there is no real Prolific study in local/mock runs, so the mock enrollment
// client reads `exp` from the URL to pick which fixture experiment to serve.
function readExperimentOverride(search = '') {
  try {
    const params = new URLSearchParams(normalizeSearch(search));
    const exp = params.get('exp');
    return isExperiment(exp) ? exp : null;
  } catch {
    return null;
  }
}

function hasFakeNotLiveFlag(search = '') {
  try {
    const params = new URLSearchParams(normalizeSearch(search));
    return params.get('fake') === '1';
  } catch {
    return false;
  }
}

// Dev/E2E affordance ONLY, mirroring readArmOverride/readExperimentOverride. In
// production the mid-experiment feature-reveal delay is server-authoritative
// (`backend.study.featureRevealDelayMinutes`); the mock client reads
// `revealMin` (minutes, fractional allowed) so a local/E2E run can exercise
// reveal without waiting for the real delay. Absent/invalid/negative -> 0
// (feature visible immediately, matching the production default).
function readRevealDelayMsOverride(search = '') {
  try {
    const params = new URLSearchParams(normalizeSearch(search));
    const raw = params.get('revealMin');
    if (raw === null) return 0;
    const minutes = Number(raw);
    if (!Number.isFinite(minutes) || minutes < 0) {
      console.warn('[enrollment] Ignoring invalid ?revealMin= override', raw);
      return 0;
    }
    return minutes * 60_000;
  } catch (error) {
    console.warn('[enrollment] Failed to parse ?revealMin= override', error);
    return 0;
  }
}

// Dev/E2E affordance ONLY, mirroring readRevealDelayMsOverride. In production
// the report-gate delay is server-authoritative
// (`backend.study.reportGateDelayMinutes`); the mock client reads
// `reportGateMin` (or legacy `reportEveryMin`) so a local/E2E run can exercise
// the gate on a short delay. Absent/invalid/negative -> 0
// (no gate delay, matching a disabled gate).
function readReportGateDelayMsOverride(search = '') {
  try {
    const params = new URLSearchParams(normalizeSearch(search));
    const key = params.has('reportGateMin') ? 'reportGateMin' : 'reportEveryMin';
    const raw = params.get(key);
    if (raw === null) return 0;
    const minutes = Number(raw);
    if (!Number.isFinite(minutes) || minutes < 0) {
      console.warn(`[enrollment] Ignoring invalid ?${key}= override`, raw);
      return 0;
    }
    return minutes * 60_000;
  } catch (error) {
    console.warn('[enrollment] Failed to parse report gate delay override', error);
    return 0;
  }
}

function enrollmentError(message, code = null) {
  const err = new Error(message);
  if (code) err.code = code;
  return err;
}

// Mock-mode-only placeholder for the App -> Qualtrics handoff base URL. In
// production this comes from backend.study.qualtricsUrlMap; mock mode has no
// real backend, so it always returns the same local placeholder for offline
// completion screen and its "Continue to the survey" link are exercisable offline.
const MOCK_QUALTRICS_URL = 'https://example.qualtrics.com/jfe/form/SV_MOCKSURVEY';

export class MockEnrollmentClient {
  constructor(search = '') {
    this.armOverride = readArmOverride(search);
    this.experimentOverride = readExperimentOverride(search);
    this.fakeNotLive = hasFakeNotLiveFlag(search);
    this.revealDelayMs = readRevealDelayMsOverride(search);
    this.reportGateDelayMs = readReportGateDelayMsOverride(search);
    this.reportIntervalMs = this.reportGateDelayMs;
  }

  pickArm(prolificId, experiment) {
    if (this.armOverride && armConfig(this.armOverride)?.experiment === experiment) {
      return this.armOverride;
    }
    const arms = armsForExperiment(experiment);
    return arms[stableIndex(prolificId, arms.length)];
  }

  async enroll({ prolificId, studyId = null, sessionId = null }) {
    if (this.fakeNotLive) {
      throw enrollmentError('This Prolific session could not be verified.', 'not_live_session');
    }
    // Prefer the URL `exp`; if an arm override was given, honor its experiment;
    // otherwise fall back to the first experiment so a bare mock URL still runs.
    const experiment =
      this.experimentOverride ??
      (this.armOverride ? armConfig(this.armOverride)?.experiment : null) ??
      EXPERIMENTS[0];
    const arm = this.pickArm(prolificId, experiment);
    return {
      participantId: stableParticipantId(prolificId),
      sessionToken: null,
      prolificId,
      studyId,
      sessionId,
      status: 'mock',
      experiment,
      arm,
      revealDelayMs: this.revealDelayMs,
      reportGateDelayMs: this.reportGateDelayMs,
      reportIntervalMs: this.reportGateDelayMs,
      qualtricsBaseUrl: MOCK_QUALTRICS_URL,
    };
  }
}

export class HttpEnrollmentClient {
  constructor(baseUrl = '') {
    this.baseUrl = baseUrl;
  }

  async _post(path, body) {
    // This is a browser-side application endpoint, not a server-side request sink.
    // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf
    const res = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Contract-Version': CONTRACT_VERSION,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      let detail = '';
      let parsed = null;
      try {
        detail = await res.text();
        parsed = detail ? JSON.parse(detail) : null;
      } catch {
        parsed = null;
      }
      if (parsed?.error === 'not_live_session') {
        throw enrollmentError(
          parsed.message || 'This Prolific session could not be verified.',
          'not_live_session'
        );
      }
      throw new Error(`HTTP ${res.status} ${path}${detail ? ` - ${detail.slice(0, 200)}` : ''}`);
    }
    return res.json();
  }

  async enroll({ prolificId, studyId = null, sessionId = null, arm = null }) {
    // No `experiment` is sent: the server derives it from the study ID using
    // backend.study.studyExperimentMap. `arm` is forwarded as a hint only and is
    // honored when the selected live-qa profile enables arm selection.
    return this._post('/api/enroll', { prolificId, studyId, sessionId, arm });
  }
}

export function pickEnrollmentClient(search = '', { appConfig = defaultAppConfig } = {}) {
  if (resolveClientMode(search, appConfig) === 'http') {
    return new HttpEnrollmentClient('');
  }
  return new MockEnrollmentClient(search);
}
